import _ from 'lodash';
import { useState } from 'react';
import AppRouter from 'common/AppRouter';
import { Flex } from 'components/base/layout-components';
import { getColor } from 'common/constants/_colors';
import { SUMMARY_PERIODS } from '../projects.consts';
import { useParams, useHistory } from 'react-router';
import { CircularProgress, Tooltip } from '@material-ui/core';
import {
  getProjectConfigData,
  PROJECTS,
  getProjectEstimationFields,
  isAllObjectValuesZero
} from '../services/projects.services';
import {
  getChartsData,
  iterationTitle,
  getTotalsFromIssuesData
} from '../services/projectExtended.services';
import IterationTooltip from '../components/IterationTooltip';
import ExtendedIterationsHeader from '../components/ExtendedIterationsHeader';
import ProjectExtendedView from '../components/ProjectExtendedView';
import {
  WithExtendedDelivery,
  WithExtendedDeliveryType,
  WithExtendedAccuracy,
  WithExtendedInvestmentProfile,
  withExtendedPeopleEffort
} from '../projects.builder.steps';
import { useProjectsLocalStorage } from '../hooks/useProjectsLocalStorage';
import PDTIssuesTable from './PDTIssuesTable';
import {
  getInvestmentColorsMap,
  getProjectTotalUnit,
  getDeliveryData
} from '../projects.builder.common';
import { FEATURE_FLAGS_NAMES } from '@linearb/linjs-common';
import useSettingsService from 'components/hooks/useSettingsService';
import {
  HoverCell,
  HoverWrapper,
  ProjectRow,
  ProjectRowSection,
  ProjectRowsHeader
} from './ProjectExtendedContent.style';

const ROW_HEIGHT = 12;
const ROW_HEIGHT_PADDED = ROW_HEIGHT + 4;

// eslint-disable-next-line max-lines-per-function
const ProjectExtendedContent = ({
  lastSelectedPeriod,
  setLastSelectedPeriod,
  selectedIteration,
  setSelectedIteration,
  projectLoading,
  allIterationsContributors,
  project,
  contributors = [],
  contributorsLoading,
  isSingleIterationView,
  isScrumProject,
  selectedPeriod,
  setSelectedPeriod,
  lastIteration,
  allIterations,
  isRetrospectView,
  iterationSummary,
  onClickTrendIteration = null
}) => {
  const { uuid: projectUuid } = useParams();
  const { getSettingOrDefinitionValue } = useSettingsService();

  const peopleEffortAvg = project.fte || project.avg_contributors || 0;

  const [iterationIndex, setIterationIndex] = useState();
  const {
    projectsExcludedIssues,
    setProjectsExcludedIssues,
    projectsEstimationFields,
    setProjectsEstimationFields
  } = useProjectsLocalStorage();

  const [selectedDeliveryState, setSelectedDeliveryState] = useState(null);

  const projectEstimationFields = getProjectEstimationFields(
    projectsEstimationFields,
    project
  );

  const pdtContextForKanbanEnabled = getSettingOrDefinitionValue({
    name: FEATURE_FLAGS_NAMES.pdtContextForKanbanEnabled
  });

  if (projectLoading && !project) {
    return <CircularProgress />;
  }

  const isLastIteration = selectedPeriod === SUMMARY_PERIODS.last_sprint;

  const projectIterations = _.get(project, 'iterations', []);

  // Chart Cell width is set to Highchart chart size, which is in px
  const tableHeadersWidth = `${
    parseInt(PROJECTS.LARGE_CHART_SIZE['width']) - 40
  }px`;
  const tableHeaderWidth =
    projectIterations &&
    `${parseFloat(tableHeadersWidth) / projectIterations.length}px`;

  const {
    issuesChartType,
    issuesChartConfig = {},
    projectConfig
  } = getProjectConfigData(project);

  const investmentColorsMap = getInvestmentColorsMap(project);
  const investmentData = getChartsData({
    project,
    totalCountsKey: 'total_investment_counts',
    totalStoryPointsKey: 'total_investment_story_points',
    countsKey: 'investment_counts',
    storyPointsKey: 'investment_story_points',
    calculationKey: _.get(projectEstimationFields, 'investment_type'),
    isScrumProject,
    forceTotal: true
  });

  const issuesData = getChartsData({
    project,
    totalCountsKey: 'total_planning_counts',
    totalStoryPointsKey: 'total_planning_story_points',
    countsKey: 'planning_counts',
    storyPointsKey: 'planning_story_points',
    isScrumProject,
    calculationKey: _.get(projectEstimationFields, 'delivery_type'),
    calculateUncompleted: isScrumProject
  });

  const issuesDataSorted = issuesChartConfig.issuesSorted({
    issuesData
  });

  const totals = getTotalsFromIssuesData(issuesData);
  const deliveryData = getDeliveryData({
    project,
    isScrumProject,
    deliveryType: _.get(projectEstimationFields, 'delivery_type'),
    totals
  });

  const noProjectData = isAllObjectValuesZero(totals);

  const closeIterationTooltip = () => {
    setIterationIndex(null);
    setSelectedIteration(null);
  };

  // TODO: fix this
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const history = useHistory();
  const isPulse = history.location.pathname === AppRouter.PULSE.path;

  const projectExtendedData = [
    isScrumProject
      ? WithExtendedAccuracy({
          totals,
          deliveryData,
          noProjectData
        })
      : WithExtendedDeliveryType({
          deliveryData,
          isScrumProject,
          noProjectData
        }),
    WithExtendedDelivery({
      issuesData,
      totals,
      projectConfig,
      issuesChartConfig,
      issuesChartType,
      project,
      projectsExcludedIssues,
      setProjectsExcludedIssues,
      selectedDeliveryState,
      setSelectedDeliveryState,
      isLastIteration,
      isSingleIterationView,
      projectsEstimationFields,
      setProjectsEstimationFields,
      projectEstimationFields,
      isScrumProject,
      noProjectData,
      selectedPeriod,
      isPulse
    }),
    WithExtendedInvestmentProfile({
      investmentData,
      investmentColorsMap,
      project,
      projectsEstimationFields,
      setProjectsEstimationFields,
      projectEstimationFields
    }),
    withExtendedPeopleEffort({
      peopleEffort: contributors,
      peopleEffortAvg,
      isFTECalculation: !!project.fte,
      uniquePeopleEffort: contributors,
      uniqueExcludedPeople: [],
      projectConfig,
      project,
      totalPeople: _.get(project, 'total_contributors'),
      selectedPeriod,
      isSingleIterationView,
      iterations: _.get(project, 'iterations', []),
      singleIteration: selectedIteration,
      isScrumProject
    })
  ];
  const isShowIssuesTable =
    isSingleIterationView &&
    (isScrumProject || (!isScrumProject && pdtContextForKanbanEnabled));

  return (
    <ProjectRow loading={projectLoading || contributorsLoading}>
      {!isSingleIterationView && (
        <HoverWrapper
          width={tableHeadersWidth}
          onMouseLeave={() => {
            closeIterationTooltip();
          }}
        >
          <Flex height='100%'>
            {projectIterations.map((iteration, index) => {
              return (
                <Flex
                  key={index}
                  height='100%'
                  grow='1'
                  justify='center'
                  width={tableHeaderWidth}
                >
                  <Tooltip
                    interactive
                    placement='left'
                    title={
                      <IterationTooltip
                        projectsEstimationFields={_.get(
                          projectsEstimationFields,
                          projectUuid
                        )}
                        totals={totals}
                        title={iterationTitle(iteration)}
                        iteration={iteration}
                        iterationIndex={iterationIndex}
                        project={project}
                        investmentColorsMap={investmentColorsMap}
                        totalNumberOfPeople={
                          _.get(allIterationsContributors, iteration.id, [])
                            .length
                        }
                        chartConfig={issuesChartConfig}
                        investmentSummary={investmentData}
                        issuesData={
                          isScrumProject ? issuesData : issuesDataSorted
                        }
                        totalSuffix={getProjectTotalUnit({ isScrumProject })}
                        contributors={_.get(
                          allIterationsContributors,
                          iteration.id,
                          []
                        )}
                        deliveryData={deliveryData}
                        isScrumProject={isScrumProject}
                        onClickTrendIteration={onClickTrendIteration}
                      />
                    }
                    key={index}
                  >
                    <HoverCell
                      data-cy='iteration-column'
                      isSelected={index === iterationIndex}
                      isClickable={
                        index === iterationIndex && !onClickTrendIteration
                      }
                      onMouseEnter={() => {
                        setIterationIndex(index);
                      }}
                      onClick={() => {
                        if (onClickTrendIteration) {
                          return;
                        }
                        if (!isScrumProject && !pdtContextForKanbanEnabled) {
                          return;
                        }

                        setSelectedIteration(iteration);
                        setLastSelectedPeriod(selectedPeriod);
                        setSelectedPeriod(
                          iteration?.id === lastIteration?.id ? 'L' : 'I'
                        );
                      }}
                    />
                  </Tooltip>
                </Flex>
              );
            })}
          </Flex>
        </HoverWrapper>
      )}

      {!isSingleIterationView && (
        <ProjectRowSection>
          <ProjectRowsHeader
            pl='medium'
            pr='medium'
            height='4.3rem'
            items='center'
          >
            {projectIterations.length > 0 && (
              <ExtendedIterationsHeader
                iterations={projectIterations}
                tableHeadersWidth={tableHeadersWidth}
                tableHeaderWidth={tableHeaderWidth}
              />
            )}
          </ProjectRowsHeader>
        </ProjectRowSection>
      )}

      <ProjectRowSection>
        <Flex direction='column' grow='1'>
          <ProjectExtendedView
            projectExtendedData={projectExtendedData}
            isSingleIterationView={isSingleIterationView}
            rowHeight={`${ROW_HEIGHT}rem`}
            isShowIssuesTable={isShowIssuesTable}
          />
        </Flex>
        {isShowIssuesTable && (
          <Flex
            minWidth={PROJECTS.LARGE_CHART_SIZE.width}
            maxWidth={PROJECTS.LARGE_CHART_SIZE.width}
            minHeight={`calc(${ROW_HEIGHT_PADDED}rem * 4)`}
            maxHeight={`calc(${ROW_HEIGHT_PADDED}rem * 4)`}
            borderLeft={`1px solid ${getColor('catskillWhite')}`}
            borderTop={`1px solid ${getColor('catskillWhite')}`}
            direction='column'
          >
            <PDTIssuesTable
              selectedDeliveryState={selectedDeliveryState}
              setSelectedDeliveryState={setSelectedDeliveryState}
              setSelectedPeriod={setSelectedPeriod}
              closeIterationTooltip={closeIterationTooltip}
              lastSelectedPeriod={lastSelectedPeriod}
              lastIteration={lastIteration}
              project={project}
              selectedIteration={selectedIteration}
              chartConfig={issuesChartConfig}
              isPulse={isPulse}
              isScrumProject={isScrumProject}
              allIterations={allIterations}
              isRetrospectView={isRetrospectView}
              iterationSummary={iterationSummary}
            />
          </Flex>
        )}
      </ProjectRowSection>
    </ProjectRow>
  );
};

export default ProjectExtendedContent;
